import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertIconXs } from '@lichtblick/icons/svg/alert/alert-xs';
import { ChevronDownIconXs } from '@lichtblick/icons/svg/chevron-down/chevron-down-xs';
import { SearchIconXs } from '@lichtblick/icons/svg/search/search-xs';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FieldFooter } from '../../atoms/field-footer';
import styles from './combo-box.module.scss';
const noop = ()=>{
// no-op
};
/**
 * `ComboBox` is an accessible, searchable dropdown component that supports dynamic option sources.
 *
 * The component calls `optionsCallback` with the current search input and expects a list of options or
 * a fallback string. Optionally, a default value can be preselected using `defaultOption`.
 *
 * The component supports both uncontrolled and controlled usage:
 * - Uncontrolled: omit `value` and optionally provide `defaultOption`
 * - Controlled: pass `value` and update it from `onChange`
 *
 * Form reset behavior:
 * - Uncontrolled mode resets selection to default props (or empty if none provided)
 * - Controlled mode resets internal open/search state, while selected value stays parent-controlled
 *
 * Use `ComboBoxStatic` for a version with a fixed list of options.
 *
 * @example
 * // Dynamic usage with async options
 * ```tsx
 * <ComboBox
 *   label="City"
 *   name="city"
 *   searchPlaceholder="Search cities"
 *   optionsCallback={async (value) => {
 *     const results = await fetchCities(value);
 *     return results.length > 0 ? results : 'No results found';
 *   }}
 *   onChange={(selected) => console.log(selected)}
 * />
 * ```
 */ export const ComboBox = ({ autoComplete, 'data-testid': testId, disabled, errorMessage, footer, inputMode, label, name, onBlur, onChange, onFocus, value, defaultOption, optionsCallback, required, searchPlaceholder, defaultSearchValue })=>{
    const id = useId();
    const labelId = `${id}-label`;
    const footerId = footer ? `${id}-footer` : undefined;
    const optionsId = `${id}-options`;
    const isControlled = value !== undefined;
    const [isOpen, setIsOpen] = useState(false);
    const [uncontrolledSelectedOption, setUncontrolledSelectedOption] = useState(null);
    const [searchValue, setSearchValue] = useState(defaultSearchValue || '');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const wasOpen = useRef(false);
    const searchRef = useRef(null);
    const comboBoxRef = useRef(null);
    const selectRef = useRef(null);
    const buttonRef = useRef(null);
    const selectedRef = useRef(null);
    const selectedOption = isControlled ? value : uncontrolledSelectedOption;
    // Extract primitives so useMemo can use stable value-based dependency comparison,
    // avoiding recreation when the parent re-renders with a new but structurally identical object.
    const defaultOptionLabel = defaultOption?.label ?? null;
    const defaultOptionValue = defaultOption?.value ?? null;
    const defaultOptionMemo = useMemo(()=>{
        if (defaultOptionLabel == null || defaultOptionValue == null) {
            return null;
        }
        return {
            label: defaultOptionLabel,
            value: defaultOptionValue
        };
    }, [
        defaultOptionLabel,
        defaultOptionValue
    ]);
    useEffect(()=>{
        // Only set the default once on initial mount. Once the user has made a selection
        // (or a default was already applied), subsequent defaultOption changes are intentionally
        // ignored — this follows standard uncontrolled input semantics.
        if (!isControlled && !uncontrolledSelectedOption) {
            if (defaultOptionMemo) {
                setUncontrolledSelectedOption(defaultOptionMemo);
            }
        }
    }, [
        defaultOptionMemo,
        isControlled,
        uncontrolledSelectedOption
    ]);
    useEffect(()=>{
        const form = selectRef.current?.form;
        if (!form) {
            return;
        }
        const onFormReset = ()=>{
            setIsOpen(false);
            setSearchValue(defaultSearchValue || '');
            setSelectedIndex(0);
            if (!isControlled) {
                setUncontrolledSelectedOption(defaultOptionMemo);
            }
        };
        form.addEventListener('reset', onFormReset);
        return ()=>{
            form.removeEventListener('reset', onFormReset);
        };
    }, [
        defaultOptionMemo,
        defaultSearchValue,
        isControlled
    ]);
    useEffect(()=>{
        if (isOpen) {
            searchRef.current?.focus();
            const index = filteredOptions.findIndex((opt)=>opt.value === selectedOption?.value);
            setSelectedIndex(Math.max(index, 0));
        }
    }, [
        filteredOptions,
        isOpen,
        selectedOption
    ]);
    useEffect(()=>{
        if (wasOpen.current && !isOpen) {
            buttonRef.current?.focus();
        }
        wasOpen.current = isOpen;
    }, [
        isOpen
    ]);
    useEffect(()=>{
        if (isOpen) {
            selectedRef.current?.scrollIntoView({
                block: 'nearest'
            });
        }
    }, [
        isOpen
    ]);
    const onClickOutside = useCallback((e)=>{
        if (comboBoxRef.current && !comboBoxRef.current.contains(e.target)) {
            setIsOpen(false);
        }
    }, []);
    useEffect(()=>{
        document.addEventListener('pointerdown', onClickOutside);
        return ()=>document.removeEventListener('pointerdown', onClickOutside);
    }, [
        onClickOutside
    ]);
    useEffect(()=>{
        let cancelled = false;
        const loadOptions = async ()=>{
            try {
                const result = await optionsCallback(searchValue);
                if (cancelled) {
                    return;
                }
                if (typeof result === 'string') {
                    setFilteredOptions([]);
                    setMessage(result);
                } else {
                    setMessage(null);
                    setFilteredOptions(result);
                }
            } finally{
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        setLoading(true);
        loadOptions();
        return ()=>{
            cancelled = true;
        };
    }, [
        optionsCallback,
        searchValue
    ]);
    const handleOptionSelect = useCallback((option)=>{
        if (!isControlled) {
            setUncontrolledSelectedOption(option);
        }
        onChange?.(option);
        setIsOpen(false);
    }, [
        isControlled,
        onChange
    ]);
    const handleInputKeyDown = useCallback((event)=>{
        switch(event.key){
            case 'ArrowDown':
                event.preventDefault();
                setSelectedIndex((prev)=>prev === null ? 0 : Math.min(filteredOptions.length - 1, prev + 1));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setSelectedIndex((prev)=>prev === null ? 0 : Math.max(0, prev - 1));
                break;
            case 'Enter':
                event.preventDefault();
                if (filteredOptions[selectedIndex] != null) {
                    handleOptionSelect(filteredOptions[selectedIndex]);
                }
                break;
            case 'Escape':
                event.preventDefault();
                setIsOpen(false);
                break;
            case 'Tab':
                setIsOpen(false);
                break;
        }
    }, [
        filteredOptions,
        handleOptionSelect,
        selectedIndex
    ]);
    const handleButtonKeyDown = useCallback((e)=>{
        if (/^[a-zA-Z0-9äöüÄÖÜß]$/.test(e.key)) {
            e.preventDefault();
            setIsOpen(true);
            searchRef.current?.focus();
            setSearchValue((prev)=>prev + e.key);
        }
    }, []);
    const options = useMemo(()=>{
        return filteredOptions.map((option, index)=>/*#__PURE__*/ _jsx(OptionItem, {
                onSelect: handleOptionSelect,
                option: option,
                selected: selectedIndex === index,
                selectedRef: index === selectedIndex ? selectedRef : undefined
            }, option.value));
    }, [
        filteredOptions,
        handleOptionSelect,
        selectedIndex
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: classNames(styles.wrapper, disabled && styles['wrapper-disabled']),
        "data-testid": testId ?? name,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                "aria-autocomplete": "list",
                "aria-controls": optionsId,
                "aria-expanded": isOpen,
                "aria-haspopup": "listbox",
                "aria-labelledby": [
                    labelId,
                    footerId
                ].filter(Boolean).join(' '),
                "aria-required": required,
                className: classNames(styles['combo-box-wrapper'], isOpen && styles.open),
                ref: comboBoxRef,
                role: "combobox",
                children: [
                    /*#__PURE__*/ _jsxs("select", {
                        "aria-hidden": "true",
                        className: styles['select-hidden'],
                        name: name,
                        ref: selectRef,
                        required: required,
                        tabIndex: -1,
                        value: selectedOption?.value ?? '',
                        onChange: noop,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                disabled: true,
                                hidden: true,
                                value: "",
                                children: "Bitte auswählen"
                            }),
                            filteredOptions.map((option)=>/*#__PURE__*/ _jsx("option", {
                                    value: option.value,
                                    children: option.label
                                }, option.value))
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.label,
                        id: labelId,
                        children: label
                    }),
                    /*#__PURE__*/ _jsxs("button", {
                        "aria-labelledby": labelId,
                        className: classNames(styles.button, selectedOption && styles['button-selected']),
                        disabled: disabled,
                        onBlur: ()=>{
                            onBlur?.(selectedOption ?? undefined);
                        },
                        onClick: ()=>!isOpen && setIsOpen(true),
                        onFocus: ()=>{
                            onFocus?.(selectedOption ?? undefined);
                        },
                        onKeyDown: handleButtonKeyDown,
                        ref: buttonRef,
                        type: "button",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: selectedOption?.label ?? ''
                            }),
                            /*#__PURE__*/ _jsx(ChevronDownIconXs, {
                                className: styles['icon-chevron']
                            })
                        ]
                    }),
                    isOpen && /*#__PURE__*/ _jsxs("div", {
                        "aria-labelledby": labelId,
                        className: styles.dropdown,
                        id: optionsId,
                        role: "listbox",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles['search-wrapper'],
                                children: [
                                    /*#__PURE__*/ _jsx(SearchIconXs, {
                                        className: styles['icon-search']
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        autoComplete: autoComplete,
                                        className: styles.search,
                                        "data-testid": "search-input",
                                        id: `${id}-search-input`,
                                        inputMode: inputMode,
                                        onChange: (e)=>setSearchValue(e.target.value),
                                        onKeyDown: handleInputKeyDown,
                                        placeholder: searchPlaceholder,
                                        ref: searchRef,
                                        value: searchValue
                                    })
                                ]
                            }),
                            loading && /*#__PURE__*/ _jsx("output", {
                                "aria-live": "polite",
                                className: styles.message,
                                children: "Bitte warten…"
                            }),
                            !loading && message && /*#__PURE__*/ _jsx("output", {
                                "aria-live": "polite",
                                className: styles.message,
                                children: message
                            }),
                            !loading && !message && filteredOptions.length > 0 && /*#__PURE__*/ _jsx("div", {
                                className: styles['scroll-container'],
                                children: options
                            })
                        ]
                    })
                ]
            }),
            footer && /*#__PURE__*/ _jsx(FieldFooter, {
                className: styles.footer,
                "data-testid": "field-footer",
                id: footerId,
                children: footer
            }),
            errorMessage && /*#__PURE__*/ _jsx(FieldFooter, {
                className: styles['error-message'],
                "data-testid": "field-error",
                error: true,
                icon: /*#__PURE__*/ _jsx(AlertIconXs, {}),
                children: errorMessage
            })
        ]
    });
};
const OptionItem = /*#__PURE__*/ memo(({ onSelect, option, selected, selectedRef })=>{
    return /*#__PURE__*/ _jsx("div", {
        className: styles['option-wrapper'],
        children: /*#__PURE__*/ _jsx("button", {
            "aria-selected": selected,
            className: styles['option-button'],
            "data-selected": selected,
            "data-testid": 'result-option',
            onClick: ()=>onSelect(option),
            ref: selected ? selectedRef : undefined,
            role: "option",
            type: "button",
            children: option.label
        })
    });
});

//# sourceMappingURL=index.js.map